import { useState } from "react";
import { Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsTooltip } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { ActionPlanTemplate } from "src/api/client-api/actionPlanTemplates/useActionPlanTemplates";
import { TemplateActionsMenu } from "./TemplateActionsMenu";

import { useTemplateLifecycleActions } from "./useTemplateLifecycleActions";
import { PublishTemplateDialog } from "./PublishTemplateDialog";
import { useTemplatePublicationBlocker } from "./useTemplatePublicationBlocker";

type Props = {
    template: Pick<
        ActionPlanTemplate,
        "id" | "name" | "ssqId" | "sectorId" | "status" | "actionCount" | "canView" | "canModify" | "canDelete"
    >;
    canCreate: boolean;
    onDeleted: () => void;
    onEdit?: () => void;
};

export const TemplateConsultationActions = (props: Props) => {
    const { template, canCreate, onDeleted, onEdit } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const lifecycle = useTemplateLifecycleActions(template.id);
    const [publishOpen, setPublishOpen] = useState(false);
    const publicationBlocker = useTemplatePublicationBlocker(template);
    const archived = template.status === "archived";

    return (
        <Stack direction="row" gap={theme.spacing(1)} alignItems="center">
            {template.canModify && (
                <>
                    <span>
                        <Button
                            disabled={archived ? lifecycle.isLoading : onEdit === undefined}
                            onClick={archived ? () => lifecycle.execute("unarchive") : onEdit}
                            variant="primary"
                            startIcon={<FoodsIcon icon={archived ? "importUpload" : "edit"} size={1} />}
                        >
                            {archived ?
                                t("settings.action_plan_templates.action.unarchive", "Unarchive")
                            :   t("settings.action_plan_templates.action.edit", "Edit")}
                        </Button>
                    </span>
                    {template.status === "draft" && (
                        <FoodsTooltip title={publicationBlocker}>
                            <span>
                                <Button
                                    disabled={Boolean(publicationBlocker)}
                                    onClick={() => setPublishOpen(true)}
                                    variant="secondary"
                                    startIcon={<FoodsIcon icon="importUpload" size={1} />}
                                >
                                    {t("settings.action_plan_templates.action.publish", "Publish")}
                                </Button>
                            </span>
                        </FoodsTooltip>
                    )}
                </>
            )}
            {publishOpen && <PublishTemplateDialog templateId={template.id} onClose={() => setPublishOpen(false)} />}
            <TemplateActionsMenu template={template} canCreate={canCreate} onDeleted={onDeleted} />
        </Stack>
    );
};
